import { cartIcon, trashBinIcon } from '@/assets';
import { useEffect } from 'react';

interface Gift {
  id: number;
  title: string;
  description: string;
  url: string;
  price: string;
  image_url: string;
  image_data?: string;
  recipient_name: string;
}

interface CartItem extends Gift {
  quantity: number;
}

interface ShoppingCartProps {
  isVisible: boolean;
  onClose: () => void;
  cartItems: CartItem[];
  onUpdateQuantity: (giftId: number, quantity: number) => void;
  onRemoveItem: (giftId: number) => void;
  onNext?: () => void;
}

export function ShoppingCart({
  isVisible,
  onClose,
  cartItems,
  onUpdateQuantity,
  onRemoveItem,
  onNext,
}: ShoppingCartProps) {
  if (!isVisible) return null;

  useEffect(() => {
    if (cartItems.length === 0) {
      onClose();
    }
  }, [cartItems.length]);

  // Group cart items by recipient
  const groupedItems = cartItems.reduce(
    (groups, item) => {
      const recipientName = item.recipient_name;
      if (!groups[recipientName]) {
        groups[recipientName] = [];
      }
      groups[recipientName].push(item);
      return groups;
    },
    {} as Record<string, CartItem[]>
  );

  return (
    <div
      className="sticky float-right md:h-screen bg-white border-b border-gray-200 top-0 z-20 w-full md:w-80 lg:w-[424px] right-0 min-h-screen transform transition-transform duration-300 ease-in-out md:transform-none flex flex-col"
      style={{ boxShadow: '0px 4px 20px 0px rgba(21, 93, 226, 0.18)' }}
    >
      {/* Cart Header */}
      <div className="bg-purple-medium text-white p-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img src={cartIcon} alt="cart" className="w-16 h-16" />
            <h3 className="text-2xl font-semibold leading-[1.2]">Gift Cart</h3>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200 text-xl md:hidden">
            ×
          </button>
        </div>
        <p className="text-sm mt-1">Your chosen gifts will appear listed here</p>
      </div>

      {/* Cart Items */}
      <div className="flex-1 overflow-y-auto">
        {cartItems.length === 0 ? (
          <p className="text-gray-500 text-center py-8">Your cart is empty</p>
        ) : (
          <div>
            {Object.entries(groupedItems).map(([recipientName, items]) => (
              <div key={recipientName}>
                {/* Recipient Section Header */}
                <div className="bg-[#3d4e30] text-base font-bold leading-[1.2] text-white text-center py-[10.5]">
                  <h4 className="font-medium">{recipientName}'s Gifts</h4>
                </div>

                {/* Items for this recipient */}
                <div className="space-y-3">
                  {items.map((item) => (
                    <div key={item.id} className="border-b border-[#00000066]">
                      <div className="flex items-center space-x-3 p-5">
                        <img
                          src={item.image_data || item.image_url}
                          alt={item.title}
                          className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h5 className="font-bold text-black text-sm leading-[1.2]">{item.title}</h5>
                          <div className="inline-block mt-3">
                            <div className="flex items-center space-x-3 border border-purple-extra-light py-2 px-3 rounded-2xl">
                              {item.quantity === 1 ? (
                                <button onClick={() => onRemoveItem(item.id)} className="cursor-pointer" title="Remove item">
                                  <img src={trashBinIcon} alt="trash bin icon" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                                  className="text-lg cursor-pointer"
                                >
                                  -
                                </button>
                              )}
                              <span className="text-sm w-8 text-center">{item.quantity}</span>
                              <button onClick={() => onUpdateQuantity(item.id, item.quantity + 1)} className="text-lg cursor-pointer">
                                +
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cart Footer - Sticky within cart */}
      {cartItems.length > 0 && (
        <div className="sticky bottom-0 mt-auto p-4 bg-white shadow-[0_-4px_20px_0_rgba(21,93,226,0.4)] flex-shrink-0">
          <button
            onClick={onNext}
            className="w-full bg-[#1b2215] text-white text-lg py-5 rounded-full font-semibold hover:opacity-90 transition-colors mb-3 cursor-pointer"
          >
            NEXT
          </button>
        </div>
      )}
    </div>
  );
}

export type { CartItem, Gift };
